import { Button, SimpleGrid } from '@mantine/core';
import { AddressCard } from './Cards/AddressCard';
import { ContactCard } from './Cards/ContactCard';
import { EmergencyContactCard } from './Cards/EmergencyContactCard';
import { PersonInfoCard } from './Cards/PersonInfoCard';
import { createFormContext, zodResolver } from '@mantine/form';
import { isEmptyForm } from '@/utils/functions';
import useClient from '@/hooks/client/useClient';
import useUpdateClient from '@/hooks/client/useUpdateClient';
import {
  updateClientSchema,
  type UpdateClient,
} from '@/schemas/API/PUT/client/request';
import useSyncFormWithClient from '@/hooks/client/useSyncFormWithClient';
import SectionErrorNotification from '@/components/Section/SectionErrorNotification';
import SectionLoadingOverlay from '@/components/Section/SectionLoadingOverlay';
import { ButtonBar } from '@/components/Form/ButtonBar/ButtonBar';
import { displayErrorOnMantineForm } from '@/utils/error/errorHandler';
import {
  goodNotification,
  okNotification,
} from '@/components/Notification/Notification';

export const [ClientFormProvider, useClientFormContext, useClientForm] =
  createFormContext<UpdateClient>();

export const ClientPersonalSection: React.FC = () => {
  const { client, error, mutate } = useClient();
  const { updateClient } = useUpdateClient();

  const form = useClientForm({
    validate: zodResolver(updateClientSchema),
    validateInputOnChange: true,
    validateInputOnBlur: true,
  });

  useSyncFormWithClient(client, form);

  if (error) return <SectionErrorNotification />;
  if (!client) return <SectionLoadingOverlay />;

  const handleSave = async (): Promise<void> => {
    if (form.validate().hasErrors) {
      console.error('Form has errors', form.validate().errors);
      return;
    }

    try {
      await updateClient(client.id, form.values);
      await mutate();
      form.reset();
      goodNotification('Změny byly uloženy.');
    } catch (err: any) {
      displayErrorOnMantineForm(err, form.setFieldError);
    }
  };

  function handleCancel(): void {
    form.reset();
    okNotification('Storno...', 'Osobní údaje jsou teď v původním stavu!');
  }

  if (!isEmptyForm(form)) {
    return (
      <ClientFormProvider form={form}>
        <SimpleGrid cols={3} spacing="md">
          <PersonInfoCard />
          <AddressCard addressType={'Servisní adresa'} />
          <AddressCard addressType={'Trvalá adresa'} />
          <EmergencyContactCard />
          <ContactCard />
        </SimpleGrid>
        <ButtonBar>
          <Button
            variant="outline"
            radius="md"
            size="md"
            className="text-lg"
            onClick={handleCancel}
          >
            Storno
          </Button>
          <Button
            radius="md"
            size="md"
            className="text-lg"
            onClick={handleSave}
          >
            Uložit
          </Button>
        </ButtonBar>
      </ClientFormProvider>
    );
  }

  return <SectionLoadingOverlay />;
};
